import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PanelTemplate from "../../../templates/Panel";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import InputSelect from "../../../components/InputSelect";
import Toggle from "../../../components/Toggle";
import Avatar from "../../../components/Avatar";
import Icon from "../../../components/Icon";
import SelectAdmin from "../../../components/selects/SelectAdmin";
import HelpTooltip from "../../../components/HelpTooltip";
import api from "../../../services/api";
import { toast } from "react-toastify";
import { renderError } from "../../../helpers/errors";
import { hasPermission } from "../../../services/auth";
import { createOptions } from "../../../helpers/selects";
import { generateUniqueId } from "../../../helpers/helpers";
import { loadEmojiPickerData } from "../../../helpers/emoji";
import { convertToMoney, convertToInt } from "../../../helpers/masks";
import { convertMoenyToInt } from "../../../helpers/conversions";
import { listAutomations } from "../../../services/automations";
import PipelineDeleteModal from "./PipelineDeleteModal";
import { RuleSentence } from "../Automations/RuleSentence";
import {
    DndContext,
    PointerSensor,
    closestCenter,
    useSensor,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    TRIGGERS,
    ACTIONS,
    actionForTrigger,
    triggerOptions,
    triggerGroupTitle,
    originOptions,
    amountOperatorOptions,
    taskTypeOptions,
    taskTypeLabel,
    taskDueOptions,
    cardModeOptions,
    cardModeLabel,
    responsibleModeOptions,
    CONDITION_TYPES,
} from "../../../seeds/crm/automations";

const DEFAULT_MAX_DAYS = "3";

const newStage = () => ({
    id: `previd-${generateUniqueId()}`,
    name: "",
    max_days_in_stage: DEFAULT_MAX_DAYS,
});

const normalizeStages = (stages = []) =>
    [...stages]
        .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
        .map((s) => ({
            id: s.id,
            name: s.name ?? "",
            max_days_in_stage: String(s.max_days_in_stage ?? DEFAULT_MAX_DAYS),
        }));

const toLocalAutomation = (a) => ({
    id: a.id,
    trigger: a.trigger,
    stage_local_id: a.pipeline_stage_id,
    conditions: Array.isArray(a.conditions) ? a.conditions : null,
    action_config: a.action_config ?? null,
    is_active: a.is_active ?? true,
});

const triggerIcon = (trigger) =>
    triggerOptions.find((t) => t.value === trigger)?.icon ?? "⚡";

const templateAutomationText = (automation) => {
    if (automation.trigger === TRIGGERS.BUDGET_CREATED) {
        return "Cria um card a cada novo orçamento";
    }
    if (automation.trigger === TRIGGERS.ORDER_CREATED) {
        return `Cria ${cardModeLabel(automation.action_config?.card_creation_mode)} a cada novo pedido`;
    }
    if (automation.trigger === TRIGGERS.CARD_ENTERED_STAGE) {
        return `Abre uma tarefa de ${taskTypeLabel(automation.action_config?.task_type).toLowerCase()} ao entrar na etapa`;
    }
    return "Automação incluída";
};

const plural = (count, singular, pluralForm) => `${count} ${count === 1 ? singular : pluralForm}`;

const SECTIONS = [
    { key: "identidade", label: "Identidade", icon: "tag" },
    { key: "etapas", label: "Etapas", icon: "kanban-view" },
    { key: "automacoes", label: "Automações", icon: "zap", permission: "edit_crm_settings" },
    { key: "acesso", label: "Acesso", icon: "people", permission: "manage_pipelines" },
];

const sectionDomId = (key) => `pipeline-section-${key}`;

const StageRow = ({ stage, index, total, automationCount, updateStageParam, deleteStage }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stage.id });
    const style = { transform: CSS.Transform.toString(transform), transition };

    return (
        <li className={`stage-row ${isDragging ? "is-dragging" : ""}`} ref={setNodeRef} style={style}>
            <button
                type="button"
                className="stage-row__handle"
                title="Arraste para reordenar"
                aria-label={`Reordenar ${index + 1}ª etapa`}
                {...attributes}
                {...listeners}
            >
                <Icon name="drag" size={16} />
            </button>
            <span className="stage-row__marker" aria-hidden="true">
                <span className="stage-row__num">{index + 1}</span>
            </span>
            <div className="stage-row__main">
                <input
                    className="stage-row__name"
                    placeholder={`Nome da ${index + 1}ª etapa`}
                    value={stage.name}
                    onChange={(e) => updateStageParam(stage.id, "name", e.target.value)}
                    aria-label={`Nome da ${index + 1}ª etapa`}
                />
                {automationCount > 0 &&
                    <span className="stage-row__auto" title="Automações que criam cards ou tarefas nesta etapa">
                        <Icon name="zap" size={11} /> {plural(automationCount, "automação", "automações")}
                    </span>
                }
            </div>
            <div className="stage-row__days">
                <span className="stage-row__days-label">Limite</span>
                <input
                    inputMode="numeric"
                    value={stage.max_days_in_stage}
                    onChange={(e) => updateStageParam(stage.id, "max_days_in_stage", convertToInt(e.target.value))}
                    aria-label={`Limite de dias da ${index + 1}ª etapa`}
                />
                <span className="stage-row__days-unit">dias</span>
                <HelpTooltip text="Prazo máximo (em dias) que um card pode ficar nesta etapa. Ao ultrapassá-lo, o card é destacado em vermelho no funil, sinalizando atraso." size={14} />
            </div>
            <button
                type="button"
                className="stage-row__delete"
                title={total <= 1 ? "A pipeline precisa de ao menos uma etapa" : "Excluir etapa"}
                aria-label="Excluir etapa"
                disabled={total <= 1}
                onClick={() => deleteStage(stage.id)}
            >
                <Icon name="delete-small" size={15} />
            </button>
        </li>
    );
};

const StartChooser = ({ templates, initialKey, onConfirm, onBack, showBack }) => {
    // null = ainda sem escolha do usuário: cai no primeiro modelo assim que
    // a lista chegar da API.
    const [chosen, setChosen] = useState(initialKey);
    const pick = chosen ?? templates[0]?.key ?? "blank";
    const setPick = setChosen;

    const template = pick === "blank" ? null : templates.find((t) => t.key === pick);

    return (
        <section className="pipeline-start">
            <aside className="pipeline-start__list" role="radiogroup" aria-label="Ponto de partida">
                <h2 className="pipeline-start__heading">Ponto de partida</h2>
                <p className="pipeline-start__sub">Escolha um modelo pronto ou comece do zero. Tudo pode ser ajustado depois.</p>
                {templates.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        role="radio"
                        aria-checked={pick === t.key}
                        className={`start-option ${pick === t.key ? "is-active" : ""}`}
                        onClick={() => setPick(t.key)}
                    >
                        <span className="start-option__icon">{t.icon}</span>
                        <span className="start-option__text">
                            <span className="start-option__name">{t.name}</span>
                            <span className="start-option__meta">
                                {plural((t.stages ?? []).length, "etapa", "etapas")}
                                {(t.automations ?? []).length > 0 && ` · ${plural(t.automations.length, "automação", "automações")}`}
                            </span>
                        </span>
                        <Icon name="arrow" size={16} />
                    </button>
                ))}
                <button
                    type="button"
                    role="radio"
                    aria-checked={pick === "blank"}
                    className={`start-option start-option--blank ${pick === "blank" ? "is-active" : ""}`}
                    onClick={() => setPick("blank")}
                >
                    <span className="start-option__icon"><Icon name="plus-circle" size={20} /></span>
                    <span className="start-option__text">
                        <span className="start-option__name">Em branco</span>
                        <span className="start-option__meta">Monte etapas e automações do seu jeito</span>
                    </span>
                    <Icon name="arrow" size={16} />
                </button>
                {showBack &&
                    <button type="button" className="pipeline-start__back" onClick={onBack}>
                        Voltar para a edição sem trocar o modelo
                    </button>
                }
            </aside>

            <div className="pipeline-start__preview">
                {template ? (
                    <>
                        <div className="start-preview__head">
                            <span className="start-preview__icon">{template.icon}</span>
                            <div>
                                <h3 className="start-preview__name">{template.name}</h3>
                                <p className="start-preview__desc">{template.description}</p>
                            </div>
                        </div>
                        <div className="start-preview__grid">
                            <div className="start-preview__block">
                                <h4>Etapas do funil</h4>
                                <ol className="start-preview__stages">
                                    {(template.stages ?? []).map((s, i) => (
                                        <li key={i}>
                                            <span className="start-preview__stage-num">{i + 1}</span>
                                            <span className="start-preview__stage-name">{s.name}</span>
                                            <span className="start-preview__stage-days">{plural(Number(s.max_days_in_stage ?? DEFAULT_MAX_DAYS), "dia", "dias")}</span>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                            <div className="start-preview__block">
                                <h4>Automações incluídas</h4>
                                {(template.automations ?? []).length === 0 &&
                                    <p className="start-preview__none">Este modelo não traz automações. Você pode criar as suas depois.</p>
                                }
                                <ul className="start-preview__autos">
                                    {(template.automations ?? []).map((a, i) => (
                                        <li key={i}>
                                            <span className="start-preview__auto-icon"><Icon name="zap" size={13} /></span>
                                            <span>{templateAutomationText(a)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="start-preview__blank">
                        <span className="start-preview__blank-icon"><Icon name="kanban-view" size={28} /></span>
                        <h3 className="start-preview__name">Pipeline em branco</h3>
                        <p className="start-preview__desc">Você começa com uma etapa vazia e define nome, etapas, automações e quem tem acesso.</p>
                    </div>
                )}
                <div className="start-preview__actions">
                    <Button
                        type="primary"
                        svg="arrow-right-white"
                        text={template ? `Usar o modelo ${template.name}` : "Começar em branco"}
                        action={() => onConfirm(template)}
                    />
                </div>
            </div>
        </section>
    );
};

/**
 * Editor de automação restrito à pipeline em edição: a coluna é escolhida
 * entre as etapas locais (por id local), funcionando mesmo com etapas ainda
 * não salvas.
 */
const AutomationEditorModal = ({ show, close, onSubmit, automation, stages }) => {
    const isEdit = !!automation;
    const [trigger, setTrigger] = useState(automation?.trigger ?? TRIGGERS.BUDGET_CREATED);
    const [stageLocalId, setStageLocalId] = useState(automation?.stage_local_id ?? null);
    const [conditions, setConditions] = useState([]);
    const [cardMode, setCardMode] = useState("per_product");
    const [responsibleMode, setResponsibleMode] = useState("order_admin");
    const [responsibleAdminId, setResponsibleAdminId] = useState(null);
    const [taskType, setTaskType] = useState("call");
    const [taskTitle, setTaskTitle] = useState("");
    const [taskDue, setTaskDue] = useState(0);

    useEffect(() => {
        if (!show) return;
        const cfg = automation?.action_config ?? {};
        setTrigger(automation?.trigger ?? TRIGGERS.BUDGET_CREATED);
        setStageLocalId(automation?.stage_local_id ?? null);
        setConditions(Array.isArray(automation?.conditions) ? automation.conditions.map((c) => ({ ...c })) : []);
        setCardMode(cfg.card_creation_mode ?? "per_product");
        setResponsibleMode(cfg.card_responsible_mode ?? "order_admin");
        setResponsibleAdminId(cfg.card_responsible_admin_id ?? null);
        setTaskType(cfg.task_type ?? "call");
        setTaskTitle(cfg.task_title ?? "");
        setTaskDue(cfg.task_due_offset_days ?? 0);
    }, [show, automation]);

    const action = actionForTrigger(trigger);
    const stageOptions = useMemo(() => createOptions(stages, "name", "id"), [stages]);

    const availableConditionTypes = useMemo(() => {
        if (trigger === TRIGGERS.BUDGET_CREATED) return [CONDITION_TYPES.ORIGIN, CONDITION_TYPES.AMOUNT];
        if (trigger === TRIGGERS.ORDER_CREATED) return [CONDITION_TYPES.AMOUNT];
        return [];
    }, [trigger]);
    const usedConditionTypes = conditions.map((c) => c.type);
    const addableTypes = availableConditionTypes.filter((t) => !usedConditionTypes.includes(t));

    const addCondition = (type) => {
        const base = type === CONDITION_TYPES.AMOUNT ? { type, operator: "gte", value: 0 } : { type, value: "site" };
        setConditions((prev) => [...prev, base]);
    };
    const updateCondition = (index, patch) => setConditions((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
    const removeCondition = (index) => setConditions((prev) => prev.filter((_, i) => i !== index));

    const handleTriggerChange = (value) => {
        setTrigger(value || TRIGGERS.BUDGET_CREATED);
        setConditions([]);
    };

    const stageName = stages.find((s) => String(s.id) === String(stageLocalId))?.name;
    const previewRule = {
        trigger,
        action,
        conditions,
        action_config:
            action === ACTIONS.CREATE_TASK
                ? { task_type: taskType, task_title: taskTitle, task_due_offset_days: taskDue }
                : { card_creation_mode: cardMode, card_responsible_mode: responsibleMode, card_responsible_admin_id: responsibleAdminId },
        pipeline: { name: "esta pipeline" },
        stage: { name: stageName ?? "coluna" },
    };

    const valid =
        !!stageLocalId &&
        (action !== ACTIONS.CREATE_TASK || !!taskType) &&
        !(trigger === TRIGGERS.ORDER_CREATED && responsibleMode === "fixed_admin" && !responsibleAdminId);

    const buildLocal = () => {
        const local = {
            id: automation?.id ?? null,
            trigger,
            stage_local_id: stageLocalId,
            conditions: conditions.length ? conditions : null,
            is_active: automation?.is_active ?? true,
        };
        if (action === ACTIONS.CREATE_TASK) {
            local.action_config = { task_type: taskType, task_title: taskTitle || null, task_due_offset_days: Number(taskDue) };
        } else if (trigger === TRIGGERS.ORDER_CREATED) {
            local.action_config = {
                card_creation_mode: cardMode,
                card_responsible_mode: responsibleMode,
                card_responsible_admin_id: responsibleMode === "fixed_admin" ? responsibleAdminId : null,
            };
        } else {
            local.action_config = null;
        }
        return local;
    };

    const handleSubmit = () => {
        if (!valid) return;
        onSubmit(buildLocal());
        close();
    };

    return (
        <Modal
            className="pipeline-rule-editor"
            title={isEdit ? "Editar automação" : "Nova automação"}
            show={show}
            close={close}
            footer={
                <Button type="primary" size="small" svg="save-white" text="Salvar automação" action={handleSubmit} disabled={!valid} />
            }
        >
            <div className="rule-editor">
                <div className="rule-editor__preview" aria-live="polite">
                    <span className="rule-editor__preview-label">Resumo da regra</span>
                    <RuleSentence rule={previewRule} />
                </div>

                <div className="rule-editor__step">
                    <h4 className="rule-editor__step-title"><em>1</em> Quando acontecer…</h4>
                    {isEdit ? (
                        <div className="rule-editor__trigger-fixed">
                            <span className="rule-editor__trigger-fixed-icon">{triggerIcon(trigger)}</span>
                            <span>{triggerOptions.find((t) => t.value === trigger)?.title ?? trigger}</span>
                        </div>
                    ) : (
                        <div className="rule-editor__triggers">
                            {triggerOptions.map((t) => (
                                <button
                                    key={t.value}
                                    type="button"
                                    className={`rule-editor__trigger-card ${trigger === t.value ? "is-selected" : ""}`}
                                    onClick={() => handleTriggerChange(t.value)}
                                >
                                    <span className="rule-editor__trigger-icon">{t.icon}</span>
                                    <span className="rule-editor__trigger-title">{t.title}</span>
                                    <span className="rule-editor__trigger-desc">{t.description}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {availableConditionTypes.length > 0 &&
                    <div className="rule-editor__step">
                        <h4 className="rule-editor__step-title"><em>2</em> Se… <span className="rule-editor__step-optional">(opcional)</span></h4>
                        {conditions.length === 0 &&
                            <p className="rule-editor__hint">Sem condições, a regra vale para todos os casos.</p>
                        }
                        {conditions.map((cond, index) => (
                            <div className="rule-editor__condition" key={`${cond.type}-${index}`}>
                                {cond.type === CONDITION_TYPES.ORIGIN ? (
                                    <div className="rule-editor__condition-field">
                                        <InputSelect label="Origem" value={cond.value} options={originOptions} change={(value) => updateCondition(index, { value })} />
                                    </div>
                                ) : (
                                    <>
                                        <div className="rule-editor__condition-field">
                                            <InputSelect label="Valor" value={cond.operator} options={amountOperatorOptions} change={(value) => updateCondition(index, { operator: value })} />
                                        </div>
                                        <div className="rule-editor__condition-field">
                                            <Input label="Valor" value={convertToMoney(String(cond.value ?? 0))} change={(value) => updateCondition(index, { value: convertMoenyToInt(value) })} />
                                        </div>
                                    </>
                                )}
                                <button type="button" className="rule-editor__remove" aria-label="Remover condição" onClick={() => removeCondition(index)}>×</button>
                            </div>
                        ))}
                        {addableTypes.length > 0 &&
                            <div className="rule-editor__add-conditions">
                                {addableTypes.includes(CONDITION_TYPES.ORIGIN) &&
                                    <Button type="link" size="small" text="+ Condição por origem" action={() => addCondition(CONDITION_TYPES.ORIGIN)} />
                                }
                                {addableTypes.includes(CONDITION_TYPES.AMOUNT) &&
                                    <Button type="link" size="small" text="+ Condição por valor" action={() => addCondition(CONDITION_TYPES.AMOUNT)} />
                                }
                            </div>
                        }
                    </div>
                }

                <div className="rule-editor__step">
                    <h4 className="rule-editor__step-title">
                        <em>{availableConditionTypes.length > 0 ? 3 : 2}</em>{" "}
                        {action === ACTIONS.CREATE_TASK ? "Então criar a tarefa…" : "Então criar o card…"}
                    </h4>
                    <div className="row">
                        <div className="col-12">
                            <InputSelect label="Na coluna" value={stageLocalId} options={stageOptions} change={(value) => setStageLocalId(value || null)} />
                        </div>
                    </div>

                    {trigger === TRIGGERS.ORDER_CREATED &&
                        <div className="row">
                            <div className="col-6">
                                <InputSelect label="Como criar o card" value={cardMode} options={cardModeOptions} change={(value) => setCardMode(value || "per_product")} />
                            </div>
                            <div className="col-6">
                                <InputSelect
                                    label="Responsável"
                                    value={responsibleMode}
                                    options={responsibleModeOptions}
                                    change={(value) => {
                                        setResponsibleMode(value || "order_admin");
                                        if (value !== "fixed_admin") setResponsibleAdminId(null);
                                    }}
                                />
                            </div>
                        </div>
                    }
                    {trigger === TRIGGERS.ORDER_CREATED && responsibleMode === "fixed_admin" &&
                        <div className="row"><div className="col-12"><SelectAdmin selectedAdmin={responsibleAdminId} setSelectedAdmin={setResponsibleAdminId} /></div></div>
                    }

                    {action === ACTIONS.CREATE_TASK &&
                        <>
                            <div className="row">
                                <div className="col-6"><InputSelect label="Tipo de tarefa" value={taskType} options={taskTypeOptions} change={(value) => setTaskType(value || "call")} /></div>
                                <div className="col-6"><InputSelect label="Prazo" value={taskDue} options={taskDueOptions} change={(value) => setTaskDue(value ?? 0)} /></div>
                            </div>
                            <div className="row"><div className="col-12"><Input label="Título da tarefa (opcional)" value={taskTitle} change={setTaskTitle} placeholder="Ex.: Ligar para confirmar o prazo" /></div></div>
                        </>
                    }
                </div>
            </div>
        </Modal>
    );
};

/**
 * Criação/edição de pipeline. Rotas: /crm/pipeline/nova e /crm/pipeline/:id/editar.
 */
export const PipelineFormPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isNew = !id;

    const canManageMembers = hasPermission("manage_pipelines");
    const canEditAutomations = hasPermission("edit_crm_settings");

    const [loading, setLoading] = useState(!isNew);
    const [view, setView] = useState(isNew ? "chooser" : "form");
    const [name, setName] = useState("");
    const [icon, setIcon] = useState(null);
    // Sem UI de propósito: a Star trabalha só com pipelines "com resultado
    // comercial" e criação manual + automática. O valor continua indo no
    // payload para o backend, que segue aceitando os outros modos.
    const [cardStatusMode, setCardStatusMode] = useState("commercial");
    const [allowManualCardCreation, setAllowManualCardCreation] = useState(true);
    const [stages, setStages] = useState(isNew ? [newStage()] : []);
    const [automations, setAutomations] = useState([]);
    const [selectedAdminIds, setSelectedAdminIds] = useState([]);
    const [allAdmins, setAllAdmins] = useState([]);
    const [members, setMembers] = useState([]);
    const [memberQuery, setMemberQuery] = useState("");

    const [templates, setTemplates] = useState([]);
    const [selectedTemplateKey, setSelectedTemplateKey] = useState(null);

    const [saving, setSaving] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [emojiPicker, setEmojiPicker] = useState(null);
    const [activeSection, setActiveSection] = useState("identidade");

    const [editorState, setEditorState] = useState({ show: false, index: null });

    const nameInputRef = useRef(null);
    const sensors = [useSensor(PointerSensor)];

    useEffect(() => {
        if (isNew) {
            api.get("/pipeline-templates")
                .then((res) => setTemplates(res.data?.resources ?? []))
                .catch(() => {});
        } else {
            api.get(`/pipeline/${id}`)
                .then((res) => {
                    const pipeline = res.data?.resource;
                    if (!pipeline) {
                        toast.error("Pipeline não encontrada");
                        navigate("/crm", { replace: true });
                        return;
                    }
                    setName(pipeline.name ?? "");
                    setIcon(pipeline.icon ?? null);
                    setCardStatusMode(pipeline.card_status_mode ?? "commercial");
                    setAllowManualCardCreation(Boolean(pipeline.allow_manual_card_creation ?? true));
                    setStages(normalizeStages(pipeline.stages));
                })
                .catch((error) => {
                    renderError(error);
                    navigate("/crm", { replace: true });
                })
                .finally(() => setLoading(false));

            if (canEditAutomations) {
                listAutomations({ pipeline_id: id })
                    .then((res) => setAutomations((res.data?.resources ?? []).map(toLocalAutomation)))
                    .catch((error) => renderError(error));
            }
            api.get(`/pipeline/${id}/admins`)
                .then((res) => {
                    const list = res.data?.resources ?? [];
                    setMembers(list);
                    setSelectedAdminIds(list.map((m) => m.id));
                })
                .catch((error) => renderError(error));
        }

        if (canManageMembers) {
            api.get("/list")
                .then((res) => {
                    const raw = res.data?.resources ?? res.data?.data ?? [];
                    setAllAdmins(Array.isArray(raw) ? raw : []);
                })
                .catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const visibleSections = useMemo(
        () => SECTIONS.filter((s) => !s.permission || hasPermission(s.permission)),
        []
    );

    // Marca no trilho a seção visível; o scroll acontece no .wrapper do painel.
    useEffect(() => {
        if (view !== "form" || loading) return undefined;
        const root = document.querySelector("#panel .wrapper");
        const targets = visibleSections
            .map((s) => document.getElementById(sectionDomId(s.key)))
            .filter(Boolean);
        if (targets.length === 0) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible[0]) {
                    setActiveSection(visible[0].target.dataset.section);
                }
            },
            { root, rootMargin: "-96px 0px -55% 0px", threshold: 0 }
        );
        targets.forEach((t) => observer.observe(t));
        return () => observer.disconnect();
    }, [view, loading, visibleSections]);

    const scrollToSection = (key) => {
        setActiveSection(key);
        document.getElementById(sectionDomId(key))?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const openEmojiPicker = () => {
        if (!emojiPicker) {
            Promise.all([import("@emoji-mart/react"), loadEmojiPickerData()])
                .then(([pickerMod, data]) => setEmojiPicker({ Picker: pickerMod.default, data }))
                .catch(() => toast.error("Não foi possível carregar os emojis"));
        }
        setShowEmojiPicker(true);
    };

    const applyTemplate = (template) => {
        setSelectedTemplateKey(template.key);
        setName((current) => (current.trim() ? current : template.name));
        setIcon(template.icon ?? null);
        setCardStatusMode(template.card_status_mode ?? "commercial");
        setAllowManualCardCreation(Boolean(template.allow_manual_card_creation ?? true));
        const mappedStages = (template.stages ?? []).map((s) => ({
            id: `previd-${generateUniqueId()}`,
            name: s.name,
            max_days_in_stage: String(s.max_days_in_stage ?? DEFAULT_MAX_DAYS),
        }));
        setStages(mappedStages.length ? mappedStages : [newStage()]);
        setAutomations(
            (template.automations ?? [])
                .map((a) => {
                    const stage = mappedStages[a.stage_index];
                    if (!stage) return null;
                    return {
                        id: null,
                        trigger: a.trigger,
                        stage_local_id: stage.id,
                        conditions: a.conditions ?? null,
                        action_config: a.action_config ?? null,
                        is_active: true,
                    };
                })
                .filter(Boolean)
        );
        setView("form");
    };

    const startBlank = () => {
        setSelectedTemplateKey("blank");
        setName("");
        setIcon(null);
        setCardStatusMode("commercial");
        setAllowManualCardCreation(true);
        setStages([newStage()]);
        setAutomations([]);
        setView("form");
        setTimeout(() => nameInputRef.current?.focus(), 0);
    };

    const confirmStart = (template) => (template ? applyTemplate(template) : startBlank());

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        setStages((prev) => {
            const oldIndex = prev.findIndex((s) => s.id === active.id);
            const newIndex = prev.findIndex((s) => s.id === over.id);
            if (oldIndex < 0 || newIndex < 0) return prev;
            return arrayMove(prev, oldIndex, newIndex);
        });
    };
    const addStage = () => setStages((prev) => [...prev, newStage()]);
    const deleteStage = (stageId) => {
        setStages((prev) => prev.filter((s) => s.id !== stageId));
        setAutomations((prev) => prev.filter((a) => String(a.stage_local_id) !== String(stageId)));
    };
    const updateStageParam = (stageId, param, value) =>
        setStages((prev) => prev.map((s) => (s.id === stageId ? { ...s, [param]: value } : s)));

    const automationCountByStage = useMemo(() => {
        const map = {};
        automations.forEach((a) => {
            const key = String(a.stage_local_id);
            map[key] = (map[key] ?? 0) + 1;
        });
        return map;
    }, [automations]);

    const openAutomationEditor = (index = null) => setEditorState({ show: true, index });
    const closeAutomationEditor = () => setEditorState({ show: false, index: null });
    const submitAutomation = (local) => {
        setAutomations((prev) => {
            if (editorState.index === null) return [...prev, local];
            return prev.map((a, i) => (i === editorState.index ? local : a));
        });
    };
    const toggleAutomation = (index) =>
        setAutomations((prev) => prev.map((a, i) => (i === index ? { ...a, is_active: !a.is_active } : a)));
    const removeAutomation = (index) => setAutomations((prev) => prev.filter((_, i) => i !== index));

    const automationGroups = useMemo(() => {
        const order = [TRIGGERS.BUDGET_CREATED, TRIGGERS.ORDER_CREATED, TRIGGERS.CARD_ENTERED_STAGE];
        return order
            .map((trigger) => ({
                trigger,
                items: automations
                    .map((automation, index) => ({ automation, index }))
                    .filter(({ automation }) => automation.trigger === trigger),
            }))
            .filter((group) => group.items.length > 0);
    }, [automations]);

    const toggleAdmin = (adminId) =>
        setSelectedAdminIds((prev) => (prev.includes(adminId) ? prev.filter((x) => x !== adminId) : [...prev, adminId]));

    const memberRows = useMemo(() => {
        const base = allAdmins.length > 0 ? allAdmins : members;
        return [...base].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    }, [allAdmins, members]);

    const filteredMemberRows = useMemo(() => {
        const query = memberQuery.trim().toLowerCase();
        if (!query) return memberRows;
        return memberRows.filter((row) =>
            `${row.name ?? ""} ${row.email ?? ""}`.toLowerCase().includes(query)
        );
    }, [memberRows, memberQuery]);

    const openCardsById = useMemo(() => {
        const map = {};
        members.forEach((m) => { map[m.id] = m.open_cards_count ?? 0; });
        return map;
    }, [members]);

    const removedWithOpenCards = useMemo(
        () => members.filter((m) => !selectedAdminIds.includes(m.id) && (m.open_cards_count ?? 0) > 0),
        [members, selectedAdminIds]
    );

    const selectAllMembers = () => setSelectedAdminIds(memberRows.map((r) => r.id));
    const clearMembers = () => setSelectedAdminIds([]);

    const buildPayload = () => {
        const payload = {
            name,
            icon,
            card_status_mode: cardStatusMode,
            allow_manual_card_creation: allowManualCardCreation,
            stages: stages.map((s) => ({
                id: String(s.id).startsWith("previd") ? null : s.id,
                name: s.name,
                max_days_in_stage: s.max_days_in_stage,
            })),
        };
        if (canManageMembers) {
            payload.admins = selectedAdminIds;
        }
        if (canEditAutomations) {
            payload.automations = automations
                .map((a) => ({ ...a, stage_index: stages.findIndex((s) => String(s.id) === String(a.stage_local_id)) }))
                .filter((a) => a.stage_index >= 0)
                .map((a) => ({
                    id: a.id ?? null,
                    trigger: a.trigger,
                    stage_index: a.stage_index,
                    conditions: a.conditions ?? null,
                    action_config: a.action_config ?? null,
                    is_active: a.is_active ?? true,
                }));
        }
        return payload;
    };

    const nameOk = name.trim().length > 0;
    const emptyStageNames = stages.some((s) => !s.name.trim());
    const stagesOk = stages.length > 0 && !emptyStageNames;
    const canSave = nameOk && stagesOk;
    const saveHint = !nameOk
        ? "Dê um nome à pipeline."
        : stages.length === 0
            ? "Adicione ao menos uma etapa."
            : emptyStageNames
                ? "Todas as etapas precisam de nome."
                : null;

    const sectionStatus = {
        identidade: nameOk ? "ok" : "pending",
        etapas: stagesOk ? "ok" : "pending",
        automacoes: automations.length > 0 ? "ok" : "neutral",
        acesso: selectedAdminIds.length > 0 ? "ok" : "neutral",
    };
    const sectionCount = {
        etapas: stages.length,
        automacoes: automations.length,
        acesso: selectedAdminIds.length,
    };

    const goBack = () => navigate("/crm");

    const save = () => {
        if (!canSave) return;
        setSaving(true);
        const payload = buildPayload();
        const request = isNew ? api.post("/pipeline", payload) : api.put(`/pipeline/${id}`, payload);
        request
            .then((res) => {
                const savedId = res?.data?.resource?.id ?? id;
                toast.success(isNew ? "Pipeline criada com sucesso" : "Pipeline atualizada com sucesso");
                if (savedId) {
                    navigate(`/crm/pipeline/${savedId}`, { replace: true });
                } else {
                    navigate("/crm", { replace: true });
                }
            })
            .catch((error) => renderError(error))
            .finally(() => setSaving(false));
    };

    const editingAutomation = editorState.index !== null ? automations[editorState.index] : null;
    const displayEmoji = icon || (name || "").trim().charAt(0).toUpperCase() || "☆";

    const renderSectionHead = (key, title, description, tools) => {
        const number = visibleSections.findIndex((s) => s.key === key) + 1;
        return (
            <header className="pipeline-panel__head">
                <span className="pipeline-panel__step">{String(number).padStart(2, "0")}</span>
                <div className="pipeline-panel__titles">
                    <h2>{title}</h2>
                    {description && <p>{description}</p>}
                </div>
                {tools && <div className="pipeline-panel__tools">{tools}</div>}
            </header>
        );
    };

    return (
        <PanelTemplate id="crm-pipeline-form">
            <div className="pipeline-editor">
                <div className="pipeline-editor__bar">
                    <div className="pipeline-editor__bar-left">
                        <button type="button" className="pipeline-editor__back" onClick={goBack} aria-label="Voltar para pipelines">
                            <Icon name="arrow" size={18} />
                        </button>
                        <nav className="pipeline-editor__crumbs" aria-label="Você está em">
                            <span>Pipelines</span>
                            <Icon name="arrow" size={13} />
                            <strong>{isNew ? "Nova pipeline" : (name || "Editar pipeline")}</strong>
                        </nav>
                    </div>
                    {view === "form" && !loading &&
                        <div className="pipeline-editor__bar-right">
                            {saveHint && <span className="pipeline-editor__hint"><Icon name="alert" size={13} /> {saveHint}</span>}
                            <Button type="secondary" size="small" text="Cancelar" action={goBack} />
                            <Button type="primary" size="small" svg="save-white" text={isNew ? "Criar pipeline" : "Salvar"} action={save} loading={saving} disabled={!canSave} />
                        </div>
                    }
                </div>

                {loading &&
                    <p className="pipeline-editor__loading">Carregando…</p>
                }

                {!loading && view === "chooser" &&
                    <StartChooser
                        templates={templates}
                        initialKey={selectedTemplateKey}
                        onConfirm={confirmStart}
                        onBack={() => setView("form")}
                        showBack={selectedTemplateKey !== null}
                    />
                }

                {!loading && view === "form" &&
                    <div className="pipeline-editor__layout">
                        <aside className="pipeline-rail">
                            <div className="pipeline-rail__card">
                                <span className="pipeline-rail__emoji" aria-hidden="true">{displayEmoji}</span>
                                <span className={`pipeline-rail__name ${nameOk ? "" : "is-empty"}`}>{nameOk ? name : "Sem nome ainda"}</span>
                                <span className="pipeline-rail__stats">
                                    <span>{plural(stages.length, "etapa", "etapas")}</span>
                                    {canEditAutomations && <span>{plural(automations.length, "automação", "automações")}</span>}
                                    {canManageMembers && <span>{plural(selectedAdminIds.length, "pessoa", "pessoas")}</span>}
                                </span>
                            </div>

                            <nav className="pipeline-rail__nav" aria-label="Seções do formulário">
                                {visibleSections.map((s, i) => (
                                    <button
                                        key={s.key}
                                        type="button"
                                        className={`pipeline-rail__item is-${sectionStatus[s.key]} ${activeSection === s.key ? "is-active" : ""}`}
                                        onClick={() => scrollToSection(s.key)}
                                    >
                                        <span className="pipeline-rail__item-num">{i + 1}</span>
                                        <span className="pipeline-rail__item-label">{s.label}</span>
                                        {sectionCount[s.key] !== undefined && <span className="pipeline-rail__item-count">{sectionCount[s.key]}</span>}
                                        <span className="pipeline-rail__item-dot" aria-hidden="true">
                                            {sectionStatus[s.key] === "ok" && <Icon name="check" size={10} strokeWidth={3} />}
                                        </span>
                                    </button>
                                ))}
                            </nav>

                            <div className="pipeline-rail__foot">
                                {isNew &&
                                    <button type="button" className="pipeline-rail__link" onClick={() => setView("chooser")}>
                                        <Icon name="kanban-view" size={14} /> Trocar modelo
                                    </button>
                                }
                                {!isNew &&
                                    <button type="button" className="pipeline-rail__link pipeline-rail__link--danger" onClick={() => setShowDelete(true)}>
                                        <Icon name="delete-small" size={14} /> Excluir pipeline
                                    </button>
                                }
                            </div>
                        </aside>

                        <div className="pipeline-editor__content">
                            <section className="pipeline-panel" id={sectionDomId("identidade")} data-section="identidade">
                                {renderSectionHead("identidade", "Identidade", "Como a pipeline aparece na lista e no menu.")}
                                <div className="pipeline-identity">
                                    <div className="pipeline-identity__emoji">
                                        <button
                                            type="button"
                                            className="pipeline-identity__emoji-btn"
                                            onClick={() => (showEmojiPicker ? setShowEmojiPicker(false) : openEmojiPicker())}
                                            aria-label="Escolher emoji da pipeline"
                                            title="Escolher emoji da pipeline"
                                        >
                                            <span className="pipeline-identity__emoji-char">{displayEmoji}</span>
                                            <span className="pipeline-identity__emoji-edit"><Icon name="edit" size={11} /></span>
                                        </button>
                                        {showEmojiPicker &&
                                            <>
                                                <div className="pipeline-identity__overlay" onClick={() => setShowEmojiPicker(false)} />
                                                <div className="pipeline-identity__popover">
                                                    {emojiPicker
                                                        ? <emojiPicker.Picker
                                                            data={emojiPicker.data}
                                                            locale="pt"
                                                            theme="light"
                                                            previewPosition="none"
                                                            skinTonePosition="none"
                                                            onEmojiSelect={(emoji) => { setIcon(emoji.native); setShowEmojiPicker(false); }}
                                                        />
                                                        : <div className="pipeline-identity__loading">Carregando…</div>
                                                    }
                                                </div>
                                            </>
                                        }
                                    </div>
                                    <div className="pipeline-identity__name">
                                        <input
                                            ref={nameInputRef}
                                            className="pipeline-identity__input"
                                            placeholder="Nome da pipeline"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            aria-label="Nome da pipeline"
                                            maxLength={80}
                                        />
                                        <span className="pipeline-identity__helper">
                                            Ex.: Comercial, Produção, Pós-venda. Clique no ícone ao lado para trocar o emoji.
                                        </span>
                                    </div>
                                </div>
                            </section>

                            <section className="pipeline-panel" id={sectionDomId("etapas")} data-section="etapas">
                                {renderSectionHead(
                                    "etapas",
                                    "Etapas do funil",
                                    "De cima para baixo, na ordem em que o card avança. Arraste pela alça para reordenar.",
                                    <Button type="secondary" size="small" svg="plus-circle" text="Nova etapa" action={addStage} />
                                )}
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={stages.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                                        <ol className="stage-list">
                                            {stages.map((stage, index) => (
                                                <StageRow
                                                    key={stage.id}
                                                    stage={stage}
                                                    index={index}
                                                    total={stages.length}
                                                    automationCount={automationCountByStage[String(stage.id)] ?? 0}
                                                    updateStageParam={updateStageParam}
                                                    deleteStage={deleteStage}
                                                />
                                            ))}
                                        </ol>
                                    </SortableContext>
                                </DndContext>
                                <button type="button" className="stage-list__add" onClick={addStage}>
                                    <span className="stage-list__add-marker"><Icon name="plus-circle" size={16} /></span>
                                    <span>Adicionar etapa ao fim do funil</span>
                                </button>
                            </section>

                            {canEditAutomations &&
                                <section className="pipeline-panel" id={sectionDomId("automacoes")} data-section="automacoes">
                                    {renderSectionHead(
                                        "automacoes",
                                        "Automações",
                                        "Regras que criam cards ou tarefas sozinhas nesta pipeline.",
                                        <Button type="secondary" size="small" svg="zap" text="Nova automação" action={() => openAutomationEditor(null)} disabled={stages.length === 0} />
                                    )}
                                    {automations.length === 0 &&
                                        <div className="pipeline-empty">
                                            <span className="pipeline-empty__icon"><Icon name="zap" size={20} /></span>
                                            <div className="pipeline-empty__text">
                                                <strong>Nenhuma automação ainda</strong>
                                                <p>Crie uma para a pipeline trabalhar sozinha. Por exemplo: abrir um card a cada novo orçamento.</p>
                                            </div>
                                            <Button type="link" size="small" text="Criar a primeira" action={() => openAutomationEditor(null)} disabled={stages.length === 0} />
                                        </div>
                                    }
                                    {automationGroups.map((group) => (
                                        <div className="rule-group" key={group.trigger}>
                                            <h4 className="rule-group__title">
                                                <span aria-hidden="true">{triggerIcon(group.trigger)}</span>
                                                {triggerGroupTitle(group.trigger)}
                                                <span className="rule-group__count">{group.items.length}</span>
                                            </h4>
                                            <ul className="rule-list">
                                                {group.items.map(({ automation, index }) => {
                                                    const stageName = stages.find((s) => String(s.id) === String(automation.stage_local_id))?.name;
                                                    const previewRule = {
                                                        trigger: automation.trigger,
                                                        action: actionForTrigger(automation.trigger),
                                                        conditions: automation.conditions,
                                                        action_config: automation.action_config,
                                                        pipeline: { name: name || "esta pipeline" },
                                                        stage: { name: stageName ?? "coluna" },
                                                    };
                                                    return (
                                                        <li className={`rule-row ${automation.is_active ? "" : "rule-row--paused"}`} key={automation.id ?? `new-${index}`}>
                                                            <button type="button" className="rule-row__body" onClick={() => openAutomationEditor(index)} title="Clique para editar">
                                                                <RuleSentence rule={previewRule} />
                                                                {!automation.is_active && <span className="rule-row__paused">Pausada</span>}
                                                            </button>
                                                            <div className="rule-row__actions">
                                                                <Toggle checked={automation.is_active} onChange={() => toggleAutomation(index)} label={automation.is_active ? "Pausar automação" : "Ativar automação"} />
                                                                <button type="button" className="rule-row__delete" aria-label="Excluir automação" title="Excluir automação" onClick={() => removeAutomation(index)}>
                                                                    <Icon name="delete-small" size={15} />
                                                                </button>
                                                            </div>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    ))}
                                </section>
                            }

                            {canManageMembers &&
                                <section className="pipeline-panel" id={sectionDomId("acesso")} data-section="acesso">
                                    {renderSectionHead(
                                        "acesso",
                                        "Quem pode acessar",
                                        "Clique nas pessoas para dar ou tirar acesso. Você entra automaticamente.",
                                        <span className="pipeline-people__bulk">
                                            <button type="button" onClick={selectAllMembers}>Todos</button>
                                            <button type="button" onClick={clearMembers}>Ninguém</button>
                                        </span>
                                    )}
                                    {memberRows.length > 5 &&
                                        <label className="pipeline-people__search">
                                            <Icon name="search" size={15} />
                                            <input
                                                placeholder="Buscar por nome ou e-mail"
                                                value={memberQuery}
                                                onChange={(e) => setMemberQuery(e.target.value)}
                                                aria-label="Buscar pessoa"
                                            />
                                            {memberQuery &&
                                                <button type="button" onClick={() => setMemberQuery("")} aria-label="Limpar busca"><Icon name="close" size={14} /></button>
                                            }
                                        </label>
                                    }
                                    {filteredMemberRows.length === 0 &&
                                        <p className="pipeline-people__none">Ninguém encontrado com “{memberQuery}”.</p>
                                    }
                                    <ul className="pipeline-people">
                                        {filteredMemberRows.map((row) => {
                                            const checked = selectedAdminIds.includes(row.id);
                                            const openCards = openCardsById[row.id] ?? 0;
                                            return (
                                                <li key={row.id}>
                                                    <button
                                                        type="button"
                                                        className={`person-tile ${checked ? "is-on" : ""}`}
                                                        aria-pressed={checked}
                                                        onClick={() => toggleAdmin(row.id)}
                                                    >
                                                        <Avatar name={row.name} photo={row.photo_url} size={36} />
                                                        <span className="person-tile__text">
                                                            <span className="person-tile__name">{row.name}</span>
                                                            <span className="person-tile__meta">{row.role_name || row.email}</span>
                                                            {openCards > 0 &&
                                                                <span className="person-tile__badge" title="Cards em aberto atribuídos a este usuário nesta pipeline">
                                                                    {plural(openCards, "card em aberto", "cards em aberto")}
                                                                </span>
                                                            }
                                                        </span>
                                                        <span className="person-tile__check" aria-hidden="true">
                                                            {checked && <Icon name="check" size={12} strokeWidth={3} />}
                                                        </span>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    {removedWithOpenCards.length > 0 &&
                                        <div className="pipeline-people__warning">
                                            <Icon name="alert" size={15} />
                                            <span>
                                                Ao salvar, {removedWithOpenCards.length === 1 ? "esta pessoa deixará" : "estas pessoas deixarão"} de ver a pipeline, incluindo cards abertos que ainda são responsabilidade {removedWithOpenCards.length === 1 ? "dela" : "delas"}. Os cards permanecem; reatribua-os se necessário.
                                            </span>
                                        </div>
                                    }
                                </section>
                            }
                        </div>
                    </div>
                }
            </div>

            {editorState.show &&
                <AutomationEditorModal
                    show={true}
                    close={closeAutomationEditor}
                    onSubmit={submitAutomation}
                    automation={editingAutomation}
                    stages={stages}
                />
            }

            {showDelete &&
                <PipelineDeleteModal
                    pipeline={{ id, name }}
                    close={() => setShowDelete(false)}
                    onDeleted={() => navigate("/crm", { replace: true })}
                />
            }
        </PanelTemplate>
    );
};

export default PipelineFormPage;
